import * as THREE from 'three';
import Application from '../Application';
import { BRUNO_DESK } from './DecorMode';
/**
 * Pose le poste de travail de la scene de reference du dossier 1
 * (my-room-in-3d, Bruno Simon) sur le bureau procedural de cette scene.
 *
 * Le modele d'origine est UN seul maillage fusionne de 104 000 triangles,
 * compresse en Draco, sans aucun objet nomme : murs, sol, bureau, ecrans et
 * accessoires y sont soudes. Il a donc fallu le decompresser, le segmenter en
 * composants connexes, puis regrouper ces composants par proximite pour
 * retrouver des objets. `desk_set.glb` est le resultat : les 71 objets de la
 * zone du bureau, un noeud nomme chacun, avec leurs sommets propres pour que
 * leurs boites englobantes soient justes.
 *
 * Le groupe est importe d'un bloc, et non objet par objet. C'est deliberé :
 * ce qui fait lire la scene comme « sa » piece, c'est la disposition — un
 * clavier decale, le mac en biais, les mugs qui s'entassent a gauche. Une
 * seule mise a l'echelle uniforme la conserve exactement ; la reposer objet
 * par objet reviendrait a inventer quarante valeurs magiques et une
 * disposition que personne n'a dessinee.
 *
 * Ce qui N'EST PAS importe, et pourquoi :
 *  - le panneau du moniteur et son pied : l'ecran de cette scene porte le
 *    site en iframe et s'adapte a la fenetre (voir ScreenConfig), alors que
 *    celui de Bruno Simon est cuit a proportions fixes ;
 *  - le bureau, le mur et le sol : ils restent ceux de DeskSet.
 *
 * Reglages a chaud, sans rebuild :
 *   ?bruno=0          revient aux objets proceduraux
 *   ?brunoScale=1.05  elargit le groupe
 *   ?brunoZ=40        le recule/avance
 *   ?brunoX=-20       le decale lateralement
 *   ?brunoLift=5      le souleve (si un objet s'enfonce dans le plateau)
 */
export default class BrunoDesk {
    constructor() {
        this.group = null;
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        if (!BRUNO_DESK)
            return;
        this.place();
    }
    params() {
        const p = new URLSearchParams(window.location.search);
        const num = (k, d) => {
            const v = p.get(k);
            const n = v === null ? NaN : parseFloat(v);
            return Number.isFinite(n) ? n : d;
        };
        return {
            scale: num('brunoScale', 1),
            dx: num('brunoX', 0),
            dz: num('brunoZ', 0),
            lift: num('brunoLift', 0),
        };
    }
    place() {
        const model = this.resources.items.gltfModel.deskSetModel;
        const texture = this.resources.items.texture.roomAtlasTexture;
        // On se cale sur le noeud baked `desk`, comme tout le reste de la
        // scene : DeskSet construit son plateau exactement a son emplacement,
        // donc sa boite reste la reference meme une fois le noeud masque.
        const desk = this.scene.getObjectByName('desk');
        if (!model || !texture || !desk)
            return;
        texture.flipY = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshBasicMaterial({ map: texture });
        desk.updateWorldMatrix(true, true);
        const deskBox = new THREE.Box3().setFromObject(desk);
        const deskW = deskBox.max.x - deskBox.min.x;
        const deskTop = deskBox.max.y;
        const group = model.scene.clone(true);
        group.traverse((child) => {
            if (child instanceof THREE.Mesh)
                child.material = material;
        });
        group.name = 'bruno_desk';
        // Le clone herite des transformations du GLB : on repart de zero pour
        // que la mise a l'echelle ci-dessous soit la seule en jeu.
        group.position.set(0, 0, 0);
        group.rotation.set(0, 0, 0);
        group.scale.setScalar(1);
        group.updateMatrixWorld(true);
        const { scale, dx, dz, lift } = this.params();
        const raw = new THREE.Box3().setFromObject(group);
        const rawW = raw.max.x - raw.min.x;
        if (rawW <= 0)
            return;
        // Mise a l'echelle uniforme sur la largeur du plateau. Uniforme, et
        // pas un facteur par axe : le poste est plus large que profond chez
        // Bruno Simon, etirer en profondeur deformerait chaque objet.
        group.scale.setScalar((deskW / rawW) * scale);
        group.updateMatrixWorld(true);
        // L'origine du groupe a ete posee, a l'export, au niveau du plateau et
        // au centre de l'emprise : la caler sur le dessus du bureau suffit
        // donc a poser les objets dessus ET a laisser passer dessous la tour
        // et les caissons.
        const placed = new THREE.Box3().setFromObject(group);
        const placedCx = (placed.min.x + placed.max.x) / 2;
        group.position.x += deskBox.min.x + deskW / 2 - placedCx + dx;
        group.position.y += deskTop + lift;
        // Les fonds alignes plutot que les centres : le poste est moins
        // profond que le plateau, centrer laisserait un vide contre le mur.
        group.position.z += deskBox.min.z - placed.min.z + dz;
        this.group = group;
        this.scene.add(group);
    }
}
